import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { listMerchants, getMerchant, createMerchant, updateMerchant, deleteMerchant } from '@/lib/api/merchants';
import type { Merchant, ListMerchantsParams, CreateMerchantParams, UpdateMerchantParams } from '@/types/merchant';

export const merchantKeys = {
  all: ['merchants'] as const,
  lists: () => [...merchantKeys.all, 'list'] as const,
  list: (params?: ListMerchantsParams) => [...merchantKeys.lists(), params] as const,
  details: () => [...merchantKeys.all, 'detail'] as const,
  detail: (id: string) => [...merchantKeys.details(), id] as const,
};

export function useMerchants(params?: ListMerchantsParams) {
  return useQuery({
    queryKey: merchantKeys.list(params),
    queryFn: () => listMerchants(params),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

export function useMerchant(merchantId: string | undefined) {
  return useQuery({
    queryKey: merchantKeys.detail(merchantId ?? ''),
    queryFn: () => getMerchant(merchantId!),
    enabled: !!merchantId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

export function useCreateMerchant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: CreateMerchantParams) => createMerchant(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: merchantKeys.lists() });
    },
  });
}

export function useUpdateMerchant(merchantId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: UpdateMerchantParams) => updateMerchant(merchantId, params),
    onMutate: async (params) => {
      if (params.status) {
        await queryClient.cancelQueries({ queryKey: merchantKeys.detail(merchantId) });
        const previous = queryClient.getQueryData<Merchant>(merchantKeys.detail(merchantId));
        queryClient.setQueryData<Merchant | undefined>(
          merchantKeys.detail(merchantId),
          (old) => old ? { ...old, status: params.status! } : old,
        );
        return { previous };
      }
      return {};
    },
    onSuccess: (data) => {
      queryClient.setQueryData(merchantKeys.detail(merchantId), data);
      queryClient.invalidateQueries({ queryKey: merchantKeys.lists() });
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(merchantKeys.detail(merchantId), context.previous);
      }
    },
  });
}

export function useDeleteMerchant(merchantId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => deleteMerchant(merchantId),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: merchantKeys.detail(merchantId) });
      const previous = queryClient.getQueryData<Merchant>(merchantKeys.detail(merchantId));
      queryClient.setQueryData<Merchant | undefined>(
        merchantKeys.detail(merchantId),
        (old) => old ? { ...old, status: 'deactivated' as const } : old,
      );
      return { previous };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: merchantKeys.detail(merchantId) });
      queryClient.invalidateQueries({ queryKey: merchantKeys.lists() });
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(merchantKeys.detail(merchantId), context.previous);
      }
    },
  });
}
